<template>
  <div
    v-if="mountPanel"
    v-show="active"
    :id="panelId"
    class="play-tab"
    role="tabpanel"
    :aria-labelledby="tabId"
  >
    <slot />
  </div>
</template>

<script>
/**
 * PlayTab — a single panel inside <PlayTabs>.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's <b-tab>.
 * The default slot is the panel body; `title` / `icon` / `description` feed the
 * nav button rendered by the parent <PlayTabs> (read reactively — no slot
 * teleporting). `disabled` greys the tab out; `lazy` (on the parent) defers
 * mounting until first activation.
 *
 * VUE3-MIGRATION: coordination is via inject('playTabs') — NOT $parent — so it
 * survives the Vue 3 move untouched. props/slots are already Vue 3-safe.
 */
let tabUid = 0

export default {
  name: 'PlayTab',
  inject: {
    playTabs: { default: null },
  },
  props: {
    /** Nav label */
    title: {
      type: String,
      default: '',
    },
    /** Optional feather-icon name shown before the label */
    icon: {
      type: String,
      default: '',
    },
    /** Optional secondary line shown under the label while active */
    description: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    tabUid += 1
    return {
      tabId: `play-tab-${tabUid}`,
      panelId: `play-tabpanel-${tabUid}`,
      hasBeenActive: false,
    }
  },
  computed: {
    active() {
      return this.playTabs ? this.playTabs.isActive(this) : true
    },
    /** With lazy parents, render only after the panel has first been shown */
    mountPanel() {
      if (!this.playTabs || !this.playTabs.lazy) return true
      return this.hasBeenActive
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(val) {
        if (val) this.hasBeenActive = true
      },
    },
  },
  created() {
    if (this.playTabs) this.playTabs.register(this)
  },
  beforeDestroy() {
    if (this.playTabs) this.playTabs.unregister(this)
  },
}
</script>

<style lang="scss" scoped>
.play-tab {
  font-family: var(--font-body);
  color: var(--text-body);
  line-height: 1.55;
  animation: play-tab-in var(--dur) var(--ease-out);
}

@keyframes play-tab-in {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}

@media (prefers-reduced-motion: reduce) {
  .play-tab { animation: none; }
}
</style>
